import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Button, Modal } from 'react-bootstrap';
import { CircleCheck, Clock, MessageCircle, Send } from 'lucide-react';
import { answerPublicQuestion } from 'redux/modules/clientATS';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import styles from './PublicQuestionsModal.module.scss';

const PublicQuestionsModal = ({ show, onHide, questions, briefId }) => {
  const [replyingToQuestionId, setReplyingToQuestionId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const dispatch = useDispatch();

  const pendingCount =
    questions?.filter((q) => !q.answer || q.answer.trim() === '').length || 0;

  const handleReplyClick = (questionId) => {
    setReplyingToQuestionId(questionId);
    setAnswerText('');
  };

  const handleCancelReply = () => {
    setReplyingToQuestionId(null);
    setAnswerText('');
  };

  const handleSubmitAnswer = (questionId) => {
    if (!answerText.trim()) return;

    setSubmittingAnswer(true);
    dispatch(answerPublicQuestion(briefId, questionId, answerText))
      .then(() => {
        setReplyingToQuestionId(null);
        setAnswerText('');
        setSubmittingAnswer(false);
      })
      .catch((error) => {
        console.error('Error submitting answer:', error);
        setSubmittingAnswer(false);
      });
  };

  return (
    <Modal show={show} onHide={onHide} bsSize="large">
      <Modal.Header closeButton className={styles.modalHeaderContainer}>
        <div className={styles.modalHeader}>
          <div>
            <Modal.Title className={styles.modalTitle}>
              Public Questions
            </Modal.Title>
            <p className={styles.modalSubtitle}>
              Potential applicants have questions. Review and respond to help
              them decide to apply.
            </p>
          </div>
          <span
            className={`${styles.headerPendingBadge} ${
              pendingCount === 0 ? styles.headerPendingBadgeNone : ''
            }`}
          >
            <Clock width={12} height={12} />
            {pendingCount > 0
              ? `${pendingCount} pending`
              : 'No pending questions'}
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.questionsList}>
          {questions && questions.length > 0 ? (
            questions.map((question) => {
              const isAnswered =
                question.answer && question.answer.trim() !== '';
              const timeAgo = question.created_at
                ? moment(question.created_at).fromNow()
                : '';
              const answerTimeAgo = question.answer
                ? moment(question.updated_at).fromNow()
                : '';

              return (
                <div key={question.id} className={styles.questionItem}>
                  <div className={styles.questionHeader}>
                    <div className={styles.questionMeta}>
                      <div className={styles.iconWrapper}>
                        <MessageCircle
                          width={16}
                          height={16}
                          className={styles.questionIcon}
                        />
                      </div>
                      <div className={styles.questionContent}>
                        <div className={styles.questionText}>
                          {question.question}
                        </div>
                        <div className={styles.questionMetaLine}>
                          {/* NOT CURRENTLY IMPLEMENTED */}
                          {/* <span className={styles.questionAuthor}>
                            {question.user?.displayname ||
                              question.user?.username ||
                              'Anonymous'}
                          </span> */}
                          {timeAgo && (
                            <span className={styles.questionTime}>
                              {timeAgo}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`${styles.statusBadge} ${
                        isAnswered
                          ? styles.statusAnswered
                          : styles.statusPending
                      }`}
                    >
                      {isAnswered ? (
                        <CircleCheck width={12} height={12} />
                      ) : (
                        <Clock width={12} height={12} />
                      )}
                      {isAnswered ? 'Answered' : 'Pending'}
                    </span>
                  </div>

                  {isAnswered && question.answer && (
                    <div className={styles.answerSection}>
                      <div className={styles.answerLabel}>
                        Your answer
                        <span className={styles.answerTime}>
                          {' • '}
                          {answerTimeAgo}
                        </span>
                      </div>
                      <div className={styles.answerText}>{question.answer}</div>
                    </div>
                  )}

                  {!isAnswered && (
                    <div className={styles.buttonWrapper}>
                      {replyingToQuestionId === question.id ? (
                        <div className={styles.replyForm}>
                          <textarea
                            className={styles.replyTextarea}
                            placeholder="Type your answer here..."
                            rows="3"
                            value={answerText}
                            onChange={(e) => setAnswerText(e.target.value)}
                            disabled={submittingAnswer}
                          />
                          <div className={styles.replyActions}>
                            <ButtonWithLoader
                              className={styles.postAnswerButton}
                              onClick={() => handleSubmitAnswer(question.id)}
                              disabled={!answerText.trim() || submittingAnswer}
                              loading={submittingAnswer}
                              bsStyle="primary"
                              icon={<Send width={14} height={14} />}
                              text={
                                submittingAnswer
                                  ? 'Posting Answer...'
                                  : 'Post Answer'
                              }
                            />
                            <Button
                              bsStyle="default"
                              className={styles.cancelButton}
                              onClick={handleCancelReply}
                              disabled={submittingAnswer}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          bsStyle="primary"
                          className={styles.replyButton}
                          onClick={() => handleReplyClick(question.id)}
                        >
                          <Send width={14} height={14} />
                          Reply
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className={styles.emptyState}>
              <p>No questions yet</p>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

PublicQuestionsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  briefId: PropTypes.number.isRequired,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      question: PropTypes.string,
      answer: PropTypes.string,
      created_at: PropTypes.string,
      user: PropTypes.shape({
        displayname: PropTypes.string,
        username: PropTypes.string,
      }),
    })
  ),
};

export default PublicQuestionsModal;
